import { Link } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { 
  Bot, 
  Code2, 
  Sparkles, 
  Shield, 
  Zap, 
  GitBranch, 
  Terminal, 
  Search,
  Brain,
  Layers
} from 'lucide-react'

export default function HomePage() {
  const { isAuthenticated } = useAuthStore()

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-dark border-b border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Bot className="w-8 h-8 text-primary-500" />
              <span className="text-xl font-bold text-white">HalCode</span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-dark-300 hover:text-white transition-colors">Features</a>
              <a href="#pricing" className="text-dark-300 hover:text-white transition-colors">Pricing</a>
              <a href="#docs" className="text-dark-300 hover:text-white transition-colors">Docs</a>
            </nav>
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <Link to="/chat" className="btn-primary">
                  Go to Chat
                </Link>
              ) : (
                <>
                  <Link to="/login" className="btn-ghost">
                    Sign In
                  </Link>
                  <Link to="/register" className="btn-primary">
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-8">
            <Sparkles className="w-4 h-4 text-primary-400" />
            <span className="text-sm text-primary-400">AI Coding Assistant v4.6</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Your AI Coding Partner
            <br />
            <span className="text-gradient">With Deep Context</span>
          </h1>
          <p className="text-xl text-dark-300 max-w-2xl mx-auto mb-10">
            HalCode understands your entire codebase, writes files, runs commands, 
            and collaborates with specialized AI agents to build software faster.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register" className="btn-primary text-lg px-8 py-3">
              Start Coding Free
            </Link>
            <a href="#features" className="btn-secondary text-lg px-8 py-3">
              See Features
            </a>
          </div>
          
          {/* Code Preview */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="rounded-xl overflow-hidden border border-dark-700 bg-dark-800 shadow-2xl">
              <div className="flex items-center gap-2 px-4 py-3 bg-dark-700 border-b border-dark-600">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="ml-2 text-sm text-dark-400">HalCode Chat</span>
              </div>
              <div className="p-6 text-left">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">Y</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-dark-200 mb-2">Build a React todo app with TypeScript</p>
                  </div>
                </div>
                <div className="flex gap-4 mt-4">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-dark-300 text-sm mb-3">
                      I'll create a complete React todo app for you. Here's my plan:
                    </p>
                    <div className="bg-dark-700 rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-dark-200">
                        <span className="text-primary-400">→</span>
                        Create App.tsx with todo logic
                      </div>
                      <div className="flex items-center gap-2 text-sm text-dark-200">
                        <span className="text-primary-400">→</span>
                        Create TodoList.tsx component
                      </div>
                      <div className="flex items-center gap-2 text-sm text-dark-200">
                        <span className="text-primary-400">→</span>
                        Add styling with CSS modules
                      </div>
                    </div>
                    <p className="text-dark-400 text-sm mt-3">Should I proceed?</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-dark-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Everything You Need to Code Smarter
            </h2>
            <p className="text-xl text-dark-400 max-w-2xl mx-auto">
              Enterprise-grade AI coding capabilities built into an intuitive interface
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<Brain className="w-6 h-6" />}
              title="200K Token Context"
              description="Understands your entire codebase, not just snippets. Automatic indexing with style guide integration."
            />
            <FeatureCard 
              icon={<Layers className="w-6 h-6" />}
              title="Multi-Agent Collaboration"
              description="Three specialized AI agents work together: Architect, Developer, and QA Engineer."
            />
            <FeatureCard 
              icon={<Terminal className="w-6 h-6" />}
              title="Terminal Integration"
              description="Run npm install, execute tests, and more directly from the AI with your approval."
            />
            <FeatureCard 
              icon={<Code2 className="w-6 h-6" />}
              title="Smart Code Completion"
              description="GitHub Copilot-style inline suggestions that understand your codebase patterns."
            />
            <FeatureCard 
              icon={<Search className="w-6 h-6" />}
              title="Semantic Search"
              description="Find code using natural language. AI-powered embeddings make search instant and accurate."
            />
            <FeatureCard 
              icon={<GitBranch className="w-6 h-6" />}
              title="Git Integration"
              description="AI-generated commit messages, diff viewing, and quick Git actions in the chat."
            />
            <FeatureCard 
              icon={<Bot className="w-6 h-6" />}
              title="MCP Tool Support"
              description="Connect external tools via Model Context Protocol. Filesystem, fetch, and shell commands."
            />
            <FeatureCard 
              icon={<Shield className="w-6 h-6" />}
              title="Your Keys, Your Data"
              description="API keys are encrypted and stored securely. Your code never leaves your control."
            />
            <FeatureCard 
              icon={<Zap className="w-6 h-6" />}
              title="Multiple AI Providers"
              description="Choose Claude, Gemini, Grok, or Ollama. Switch anytime based on your needs."
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-dark-400">
              Pay only for what you use. No hidden fees.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <PricingCard 
              name="Free"
              price="$0"
              description="Perfect for trying out HalCode"
              features={[
                "50 AI messages per day",
                "10 inline completions",
                "5 terminal commands",
                "Codebase indexing",
                "Semantic search",
                "Community support"
              ]}
              highlighted={false}
            />
            <PricingCard 
              name="Pro"
              price="$19"
              period="/month"
              description="For professional developers"
              features={[
                "Unlimited AI messages",
                "Unlimited completions",
                "Unlimited terminal commands",
                "Multi-Agent workflows",
                "MCP tool support",
                "Priority support",
                "Early access to new features"
              ]}
              highlighted={true}
            />
            <PricingCard 
              name="Team"
              price="$49"
              period="/month"
              description="For development teams"
              features={[
                "Everything in Pro",
                "5 team members",
                "Shared codebases",
                "Team analytics",
                "Admin dashboard",
                "SSO integration",
                "Dedicated support"
              ]}
              highlighted={false}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Code Smarter?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Join thousands of developers using HalCode to build software faster.
          </p>
          <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition-colors">
            Get Started Free
            <Sparkles className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-dark-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Bot className="w-6 h-6 text-primary-500" />
              <span className="font-bold text-white">HalCode</span>
            </div>
            <p className="text-dark-400 text-sm">
              © 2025 HalCode AI Assistant. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-dark-400 hover:text-white transition-colors text-sm">Privacy</a>
              <a href="#" className="text-dark-400 hover:text-white transition-colors text-sm">Terms</a>
              <a href="#" className="text-dark-400 hover:text-white transition-colors text-sm">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

function FeatureCard({ 
  icon, 
  title, 
  description 
}: { 
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="card-hover group">
      <div className="w-12 h-12 rounded-lg bg-primary-500/10 flex items-center justify-center text-primary-400 mb-4 group-hover:bg-primary-500/20 transition-colors">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-dark-400">{description}</p>
    </div>
  )
}

function PricingCard({ 
  name, 
  price, 
  period, 
  description, 
  features, 
  highlighted 
}: { 
  name: string
  price: string
  period?: string
  description: string
  features: string[]
  highlighted: boolean
}) {
  return (
    <div className={`card relative ${highlighted ? 'border-primary-500 ring-2 ring-primary-500/20' : ''}`}>
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-full">
          Most Popular
        </div>
      )}
      <h3 className="text-xl font-bold text-white mb-2">{name}</h3>
      <div className="flex items-baseline gap-1 mb-2">
        <span className="text-4xl font-bold text-white">{price}</span>
        {period && <span className="text-dark-400">{period}</span>}
      </div>
      <p className="text-dark-400 text-sm mb-6">{description}</p>
      <ul className="space-y-3 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2 text-sm">
            <svg className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-dark-300">{feature}</span>
          </li>
        ))}
      </ul>
      <Link 
        to="/register" 
        className={`w-full text-center ${highlighted ? 'btn-primary' : 'btn-secondary'}`}
      >
        Get Started
      </Link>
    </div>
  )
}
